/**
 * Popup global do Vigia de esgotados (pedido do Felipe, 28/10/2026): montado no BiLayout, aparece em qualquer página
 * quando algum anúncio de um SKU que acabou de vez está VENDÁVEL. Consulta o serviço a cada 60 s. Nada é pausado
 * sozinho: o botão "Pausar" é o clique que aprova. "Ciente" silencia o alerta por algumas horas (definido no serviço);
 * fechar no X esconde até aparecer alerta novo ou passar 15 min.
 * Se o serviço não estiver no ar (404/502), o popup fica quieto — a página /vigia-esgotados mostra o erro.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, Stack, Typography,
} from '@mui/material'
import { ciente, fetchAlertas, fetchStatus, pausar, type Acao, type AlertaVigia } from '../pages/vigiaEsgotados/vigiaService'
import { ROTULO_ACAO, avisoConfirmacao, descricaoSku, logistica, loja, quando, semAcao } from '../pages/vigiaEsgotados/logic'

const POLL_MS = 60_000
const REABRIR_MS = 15 * 60_000

/** Botões de um alerta aberto (popup e aba Alertas da página). */
export function BotoesAlerta({ a, escreve, onFeito }: { a: AlertaVigia; escreve: boolean; onFeito: (msg: string, ok: boolean) => void }) {
  const [busy, setBusy] = useState<string | null>(null)
  const rodar = async (nome: string, fn: () => Promise<{ ok: boolean; mensagem?: string }>) => {
    setBusy(nome)
    try {
      const r = await fn()
      onFeito(r.mensagem || (r.ok ? 'feito' : 'falhou'), r.ok)
    } catch (e) {
      onFeito(e instanceof Error ? e.message : String(e), false)
    } finally {
      setBusy(null)
    }
  }
  const feito = [...a.acoes].reverse().find((x) => x.ok && x.tipo !== 'ciente')
  if (feito) {
    return (
      <Typography variant="caption" sx={{ color: 'success.main' }}>
        {feito.tipo === 'zerar_variacao' ? 'Variação zerada' : 'Pausado'} por {feito.por} em {quando(feito.em)} — a próxima leitura confirma e fecha o alerta.
      </Typography>
    )
  }
  if (!escreve) return <Typography variant="caption" color="text.secondary">serviço em modo leitura</Typography>
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
      {a.acoes_possiveis.length === 0 ? <Typography variant="caption" color="text.secondary">{semAcao(a)}</Typography> : null}
      {a.acoes_possiveis.map((acao: Acao) => (
        <Button key={acao} size="small" color="error" variant={acao === 'pausar_anuncio' ? 'contained' : 'outlined'} disabled={busy != null}
          onClick={() => {
            if (!window.confirm(`${avisoConfirmacao(a, acao)}\n\nConfirmar: ${ROTULO_ACAO[acao](a.marketplace)}`)) return
            void rodar(acao, () => pausar(a.id, acao))
          }}>
          {busy === acao ? <CircularProgress size={14} /> : ROTULO_ACAO[acao](a.marketplace)}
        </Button>
      ))}
      <Button size="small" variant="text" disabled={busy != null} onClick={() => void rodar('ciente', async () => ({ ...(await ciente(a.id)), mensagem: 'silenciado' }))}>
        Ciente
      </Button>
    </Stack>
  )
}

export function LinhaAlerta({ a }: { a: AlertaVigia }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={loja(a.loja)} />
        {a.full ? <Chip size="small" label={logistica(a)} variant="outlined" /> : null}
        <Link href={a.link || undefined} target="_blank" rel="noopener" variant="body2" sx={{ fontWeight: 600 }}>
          {a.item_id}
        </Link>
        <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>{a.titulo}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.25 }}>{descricaoSku(a)}</Typography>
      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>{a.motivo}</Typography>
      <Typography variant="caption" color="text.secondary">aberto em {quando(a.aberto_em)} · visto vendável em {quando(a.visto_em)}</Typography>
    </Box>
  )
}

export function VigiaEsgotadosPopup() {
  const [alertas, setAlertas] = useState<AlertaVigia[]>([])
  const [escreve, setEscreve] = useState(false)
  const [aberto, setAberto] = useState(false)
  const [aviso, setAviso] = useState<{ msg: string; ok: boolean } | null>(null)
  const fechado = useRef<{ ids: Set<string>; em: number } | null>(null)

  const carregar = useCallback(async () => {
    try {
      const [lista, st] = await Promise.all([fetchAlertas('popup'), fetchStatus()])
      setAlertas(lista)
      setEscreve(st.escrita)
      const f = fechado.current
      const novo = !f || lista.some((a) => !f.ids.has(a.id)) || Date.now() - f.em > REABRIR_MS
      setAberto(lista.length > 0 && novo)
    } catch {
      // serviço fora do ar ou ainda não ativado: o popup não incomoda; a página do vigia mostra o erro
    }
  }, [])

  useEffect(() => {
    void carregar()
    const t = window.setInterval(() => void carregar(), POLL_MS)
    return () => window.clearInterval(t)
  }, [carregar])

  const fechar = () => {
    fechado.current = { ids: new Set(alertas.map((a) => a.id)), em: Date.now() }
    setAberto(false)
  }

  return (
    <Dialog
      open={aberto && alertas.length > 0}
      onClose={fechar}
      maxWidth="md"
      fullWidth
      // AppBar do BiLayout está em modal+1 (header clicável); sem isto o popup fica por baixo.
      sx={{ zIndex: (t) => t.zIndex.modal + 4 }}
    >
      <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
        {alertas.length === 1 ? '1 anúncio de SKU esgotado está vendável' : `${alertas.length} anúncios de SKU esgotado estão vendáveis`}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Estes SKUs estão na lista de esgotados (não vão ter estoque nunca mais). Se venderem, o pedido vira cancelamento.
          Pausar muda o anúncio no marketplace na hora.
        </Typography>
        {aviso ? <Alert severity={aviso.ok ? 'success' : 'error'} onClose={() => setAviso(null)} sx={{ mb: 1.5 }}>{aviso.msg}</Alert> : null}
        <Stack spacing={1.5} divider={<Divider flexItem />}>
          {alertas.map((a) => (
            <Stack key={a.id} spacing={1}>
              <LinhaAlerta a={a} />
              <BotoesAlerta a={a} escreve={escreve} onFeito={(msg, ok) => { setAviso({ msg, ok }); void carregar() }} />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button component={RouterLink} to="/vigia-esgotados" onClick={fechar}>Abrir o vigia</Button>
        <Button onClick={fechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
